import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import styles from './analytics-dashboard.module.css';
import {
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../systems/auth-context';
import { apiFetch } from '../../systems/api-client';
import { getActiveWorkspaceHeaders, useWorkspace } from '../../systems/workspace-context';
import {
  canManageEnterpriseUsers,
  canActivateEnterpriseUsers,
  canSeeUnreleasedStories,
} from '../../systems/roles';
import { DEFAULT_DEPARTMENTS, getDepartmentLabel, type DepartmentOption } from '../../systems/user-departments';
import DepartmentLabelsEditor from './DepartmentLabelsEditor';
import { useLeaderboardEnabled } from '../../systems/use-leaderboard-enabled';
import { resetPlayerProfile } from '../../systems/leaderboard-api';

// ── Types ──────────────────────────────────────────────────────

interface AnalyticsUser {
  id: string;
  email: string;
  department: string | null;
  active?: number;
  /** Pseudo du classement (workspaces qui l'ont activé). */
  pseudo?: string | null;
}

interface Story {
  id: string;
  name: string;
  releaseToProd?: boolean;
}

interface WeeklyLaunch {
  week: string;
  cumulative: number;
}

interface WeeklyActive {
  week: string;
  players: number;
}

interface MissionErrorItem {
  missionId: string;
  missionLabel: string;
  errorRate: number;
}

interface ScenarioErrorItem {
  scenarioId: string;
  scenarioName: string;
  errorRate: number;
}

interface RadarDataItem {
  key: string;
  label: string;
  byDepartment: { key: string; label: string; score: number }[];
  groupAverage: number;
}

interface Enterprise {
  name: string;
  admin_code: string;
}

interface PlayerStat {
  email: string;
  scenario_id: string;
  playtime_seconds: number;
  progress_percent: number;
  last_scene_id: string;
  updated_at: string;
}

interface AvgProgressByScenario {
  scenarioId: string;
  scenarioName: string;
  progress: number;
}

interface AvgProgressByMission {
  missionId: string;
  missionLabel: string;
  progress: number;
}

interface AnalyticsResponse {
  stories: Story[];
  scenarioId: string | null;
  enterprise: Enterprise;
  playerStats: PlayerStat[];
  avgProgressByScenario: AvgProgressByScenario[];
  avgProgressByMission: AvgProgressByMission[];
  kpis: {
    total: number;
    byDepartment: { key: string; label: string; count: number }[];
  };
  weeklyLaunches: WeeklyLaunch[];
  weeklyActive: WeeklyActive[];
  scenarioErrors: ScenarioErrorItem[];
  missionErrors: MissionErrorItem[];
  radarData: RadarDataItem[];
  users: AnalyticsUser[];
}

type UserTableMode = 'gestion' | 'stats';
type StatsSortColumn = 'email' | 'scenario' | 'playtime' | 'progress' | 'updated_at';
type SortDirection = 'asc' | 'desc';

const TABLE_PAGE_SIZE = 20;
const NO_DEPARTMENT_FILTER = '__none__';

interface GestionFilters {
  email: string;
  department: string;
  status: '' | 'active' | 'pending';
}

interface StatsFilters {
  email: string;
  scenarioId: string;
  playtime: '' | 'short' | 'medium' | 'long';
  progress: '' | '0' | '1-49' | '50-99' | '100';
  updatedAt: string;
}

const EMPTY_GESTION_FILTERS: GestionFilters = {
  email: '',
  department: '',
  status: '',
};

const EMPTY_STATS_FILTERS: StatsFilters = {
  email: '',
  scenarioId: '',
  playtime: '',
  progress: '',
  updatedAt: '',
};

const COLORS = {
  primary: '#3b82f6',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  categories: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#f97316', '#ec4899'],
  average: '#ffffff',
};

function categoryColor(index: number): string {
  return COLORS.categories[index % COLORS.categories.length] ?? COLORS.primary;
}

interface RadarDatum {
  axis: string;
  fullMark: 100;
  [key: string]: string | number;
}

// ── Tooltip styles ─────────────────────────────────────────────

const tooltipStyle = {
  contentStyle: {
    backgroundColor: 'rgba(15, 15, 20, 0.95)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '0.85rem',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  },
  itemStyle: { color: '#d1d5db' },
};

// ── Custom Hex Radar ──────────────────────────────────────────

function polarToXY(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

const HEX_VERTEX_ANGLES = [30, 90, 150, 210, 270, 330];
const HEX_SIDE_ANGLES = [0, 60, 120, 180, 240, 300];
const HEX_GRID_LEVELS = [0.2, 0.4, 0.6, 0.8, 1.0];

const SECTION_BG: [string, string][] = [
  ['rgba(239, 68, 68, 0.16)', 'rgba(239, 68, 68, 0.30)'],
  ['rgba(244, 114, 182, 0.14)', 'rgba(244, 114, 182, 0.28)'],
  ['rgba(52, 211, 153, 0.14)', 'rgba(52, 211, 153, 0.28)'],
  ['rgba(74, 222, 128, 0.14)', 'rgba(74, 222, 128, 0.28)'],
  ['rgba(96, 165, 250, 0.14)', 'rgba(96, 165, 250, 0.28)'],
  ['rgba(167, 139, 250, 0.14)', 'rgba(167, 139, 250, 0.28)'],
];

interface HexRadarChartProps {
  data: RadarDatum[];
  categories: string[];
  categoryColors: string[];
  visibleCategories: string[];
}

const HexRadarChart: React.FC<HexRadarChartProps> = ({
  data, categories, categoryColors, visibleCategories,
}) => {
  const W = 560;
  const H = 500;
  const cx = W / 2;
  const cy = H / 2 + 4;
  const R = 175;
  const apothem = R * Math.cos(Math.PI / 6);

  const hexVerts = (s: number) =>
    HEX_VERTEX_ANGLES.map(a => polarToXY(cx, cy, R * s, a));

  const pts = (arr: { x: number; y: number }[]) =>
    arr.map(p => `${p.x},${p.y}`).join(' ');

  const outerV = hexVerts(1);

  const dataPoly = (key: string) =>
    data.map((d, i) =>
      polarToXY(cx, cy, ((d[key] as number) / 100) * apothem, HEX_SIDE_ANGLES[i])
    );

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={styles.hexSvg}>
      {/* Section backgrounds */}
      {SECTION_BG.map(([fill, stroke], i) => {
        const v1 = outerV[(i + 5) % 6];
        const v2 = outerV[i];
        return (
          <polygon key={`sec-${i}`}
            points={`${cx},${cy} ${v1.x},${v1.y} ${v2.x},${v2.y}`}
            fill={fill} stroke={stroke} strokeWidth={0.5}
          />
        );
      })}

      {/* Concentric hex grids */}
      {HEX_GRID_LEVELS.map((lv, li) => (
        <polygon key={`g-${li}`} points={pts(hexVerts(lv))}
          fill="none" stroke="rgba(255,255,255,0.10)"
          strokeWidth={lv === 1 ? 1.5 : 0.6}
        />
      ))}

      {/* Spoke lines center → vertex */}
      {outerV.map((v, i) => (
        <line key={`sp-${i}`} x1={cx} y1={cy} x2={v.x} y2={v.y}
          stroke="rgba(255,255,255,0.07)" strokeWidth={0.6} />
      ))}

      {/* Category data polygons */}
      {visibleCategories.map(cat => {
        const p = dataPoly(cat);
        const ci = categories.indexOf(cat);
        const col = ci >= 0 && categoryColors.length > 0
          ? (categoryColors[ci % categoryColors.length] ?? '#fff')
          : '#fff';
        return (
          <g key={cat}>
            <polygon points={pts(p)} fill={col} fillOpacity={0.10}
              stroke={col} strokeWidth={2.2} strokeLinejoin="round" />
            {p.map((pt, pi) => (
              <circle key={pi} cx={pt.x} cy={pt.y} r={4.5}
                fill={col} stroke="#0f0f14" strokeWidth={2} />
            ))}
          </g>
        );
      })}

      {/* Group average polygon */}
      {(() => {
        const p = dataPoly('Moyenne groupe');
        return (
          <g>
            <polygon points={pts(p)} fill="rgba(255,255,255,0.04)"
              stroke="rgba(255,255,255,0.65)" strokeWidth={2}
              strokeDasharray="6 3" strokeLinejoin="round" />
            {p.map((pt, pi) => (
              <circle key={pi} cx={pt.x} cy={pt.y} r={3}
                fill="#fff" stroke="rgba(255,255,255,0.3)" strokeWidth={1.5} />
            ))}
          </g>
        );
      })()}

      {/* Labels on each side */}
      {data.map((d, i) => {
        const angle = HEX_SIDE_ANGLES[i];
        const labelR = apothem + 32;
        const pos = polarToXY(cx, cy, labelR, angle);
        const avgVal = d['Moyenne groupe'] as number;

        let anchor = 'middle';
        let dx = 0;
        let dy = 0;

        if (angle === 0) { dy = -6; }
        else if (angle === 180) { dy = 8; }
        else if (angle > 0 && angle < 180) { anchor = 'start'; dx = 4; }
        else { anchor = 'end'; dx = -4; }

        return (
          <g key={`lbl-${i}`}>
            <text x={pos.x + dx} y={pos.y + dy}
              textAnchor={anchor} fill="rgba(255,255,255,0.85)"
              fontSize={12.5} fontWeight={600} fontFamily="Inter, sans-serif">
              {d.axis}
            </text>
            <text x={pos.x + dx} y={pos.y + dy + 16}
              textAnchor={anchor} fill="rgba(255,255,255,0.40)"
              fontSize={11} fontFamily="Inter, sans-serif">
              {avgVal}%
            </text>
          </g>
        );
      })}

      {/* Grid percentage labels along the 0° axis */}
      {HEX_GRID_LEVELS.slice(0, -1).map(lv => {
        const pos = polarToXY(cx, cy, apothem * lv, 30);
        return (
          <text key={`pct-${lv}`} x={pos.x + 5} y={pos.y + 3}
            fill="rgba(255,255,255,0.20)" fontSize={9}
            fontFamily="Inter, sans-serif" textAnchor="start">
            {Math.round(lv * 100)}%
          </text>
        );
      })}
    </svg>
  );
};

// ── Component ──────────────────────────────────────────────────

const AnalyticsDashboard: React.FC = () => {
  const { user: authUser } = useAuth();
  const { workspaceId } = useWorkspace();
  const showGestionTab = canManageEnterpriseUsers(authUser);
  const canToggleUserActive = canActivateEnterpriseUsers(authUser);
  const [stories, setStories] = useState<Story[]>([]);
  const [selectedStory, setSelectedStory] = useState<string>('');
  const [onlyPlayerVisibleStories, setOnlyPlayerVisibleStories] = useState(true);
  const [enterprise, setEnterprise] = useState<Enterprise | null>(null);
  const [playerStats, setPlayerStats] = useState<PlayerStat[]>([]);
  const [avgProgressByScenario, setAvgProgressByScenario] = useState<AvgProgressByScenario[]>([]);
  const [avgProgressByMission, setAvgProgressByMission] = useState<AvgProgressByMission[]>([]);
  const [users, setUsers] = useState<AnalyticsUser[]>([]);
  const [kpis, setKpis] = useState<AnalyticsResponse['kpis']>({ total: 0, byDepartment: [] });
  const [weeklyLaunches, setWeeklyLaunches] = useState<WeeklyLaunch[]>([]);
  const [weeklyActive, setWeeklyActive] = useState<WeeklyActive[]>([]);
  const [scenarioErrors, setScenarioErrors] = useState<ScenarioErrorItem[]>([]);
  const [missionErrors, setMissionErrors] = useState<MissionErrorItem[]>([]);
  const [radarDataApi, setRadarDataApi] = useState<RadarDataItem[]>([]);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editingDepartment, setEditingDepartment] = useState<string>('');
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [userTableMode, setUserTableMode] = useState<UserTableMode>(
    canManageEnterpriseUsers(authUser) ? 'gestion' : 'stats'
  );
  const [activatingUserId, setActivatingUserId] = useState<string | null>(null);
  const [resettingPseudoId, setResettingPseudoId] = useState<string | null>(null);
  const leaderboardEnabled = useLeaderboardEnabled();
  const [statsSortColumn, setStatsSortColumn] = useState<StatsSortColumn>('updated_at');
  const [statsSortDirection, setStatsSortDirection] = useState<SortDirection>('desc');
  const [gestionFilters, setGestionFilters] = useState<GestionFilters>(EMPTY_GESTION_FILTERS);
  const [statsFilters, setStatsFilters] = useState<StatsFilters>(EMPTY_STATS_FILTERS);
  const [gestionPage, setGestionPage] = useState(1);
  const [statsPage, setStatsPage] = useState(1);
  const [departmentOptions, setDepartmentOptions] = useState<DepartmentOption[]>(() =>
    DEFAULT_DEPARTMENTS.map((dept) => ({ ...dept }))
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userUpdateError, setUserUpdateError] = useState<string | null>(null);
  const initialStorySet = useRef(false);

  const storyNameById = useMemo(
    () => new Map(stories.map((story) => [story.id, story.name])),
    [stories]
  );

  const showStoryVisibilityFilter = canSeeUnreleasedStories(authUser)
    && stories.some((story) => story.releaseToProd === false);

  const storiesForSelector = useMemo(() => {
    if (!onlyPlayerVisibleStories) return stories;
    return stories.filter((story) => story.releaseToProd !== false);
  }, [stories, onlyPlayerVisibleStories]);

  const progressChartData = useMemo(() => {
    if (selectedStory) {
      return avgProgressByMission.map((item) => ({
        name: item.missionLabel,
        progress: item.progress,
      }));
    }
    return avgProgressByScenario.map((item) => ({
      name: item.scenarioName,
      progress: item.progress,
    }));
  }, [selectedStory, avgProgressByMission, avgProgressByScenario]);

  const errorChartData = useMemo(() => {
    if (selectedStory) {
      return missionErrors.map((item) => ({
        name: item.missionLabel,
        errorRate: item.errorRate,
      }));
    }
    return scenarioErrors.map((item) => ({
      name: item.scenarioName,
      errorRate: item.errorRate,
    }));
  }, [selectedStory, missionErrors, scenarioErrors]);

  const filteredGestionUsers = useMemo(() => {
    const emailQuery = gestionFilters.email.trim().toLowerCase();
    return users.filter((user) => {
      if (emailQuery && !user.email.toLowerCase().includes(emailQuery)) return false;
      if (gestionFilters.department === NO_DEPARTMENT_FILTER) {
        if (user.department) return false;
      } else if (gestionFilters.department && user.department !== gestionFilters.department) {
        return false;
      }
      if (gestionFilters.status === 'active' && user.active === 0) return false;
      if (gestionFilters.status === 'pending' && user.active !== 0) return false;
      return true;
    });
  }, [users, gestionFilters]);

  const filteredSortedPlayerStats = useMemo(() => {
    const emailQuery = statsFilters.email.trim().toLowerCase();
    const updatedQuery = statsFilters.updatedAt.trim().toLowerCase();
    const filtered = playerStats.filter((stat) => {
      if (emailQuery && !stat.email.toLowerCase().includes(emailQuery)) return false;
      if (statsFilters.scenarioId && stat.scenario_id !== statsFilters.scenarioId) return false;
      if (statsFilters.playtime) {
        const seconds = stat.playtime_seconds;
        if (statsFilters.playtime === 'short' && seconds >= 1800) return false;
        if (statsFilters.playtime === 'medium' && (seconds < 1800 || seconds > 7200)) return false;
        if (statsFilters.playtime === 'long' && seconds <= 7200) return false;
      }
      if (statsFilters.progress) {
        const progress = stat.progress_percent;
        if (statsFilters.progress === '0' && progress !== 0) return false;
        if (statsFilters.progress === '1-49' && (progress < 1 || progress > 49)) return false;
        if (statsFilters.progress === '50-99' && (progress < 50 || progress > 99)) return false;
        if (statsFilters.progress === '100' && progress !== 100) return false;
      }
      if (
        updatedQuery
        && !new Date(stat.updated_at).toLocaleString().toLowerCase().includes(updatedQuery)
      ) {
        return false;
      }
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (statsSortColumn) {
        case 'email':
          cmp = a.email.localeCompare(b.email, 'fr', { sensitivity: 'base' });
          break;
        case 'scenario': {
          const nameA = storyNameById.get(a.scenario_id) ?? a.scenario_id;
          const nameB = storyNameById.get(b.scenario_id) ?? b.scenario_id;
          cmp = nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
          break;
        }
        case 'playtime':
          cmp = a.playtime_seconds - b.playtime_seconds;
          break;
        case 'progress':
          cmp = a.progress_percent - b.progress_percent;
          break;
        case 'updated_at':
          cmp = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
          break;
      }
      return statsSortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [playerStats, statsFilters, statsSortColumn, statsSortDirection, storyNameById]);

  const gestionTotalPages = Math.max(1, Math.ceil(filteredGestionUsers.length / TABLE_PAGE_SIZE));
  const gestionSafePage = Math.min(gestionPage, gestionTotalPages);
  const paginatedGestionUsers = filteredGestionUsers.slice(
    (gestionSafePage - 1) * TABLE_PAGE_SIZE,
    gestionSafePage * TABLE_PAGE_SIZE
  );
  const gestionRangeStart = filteredGestionUsers.length === 0
    ? 0
    : (gestionSafePage - 1) * TABLE_PAGE_SIZE + 1;
  const gestionRangeEnd = Math.min(gestionSafePage * TABLE_PAGE_SIZE, filteredGestionUsers.length);

  const statsTotalPages = Math.max(1, Math.ceil(filteredSortedPlayerStats.length / TABLE_PAGE_SIZE));
  const statsSafePage = Math.min(statsPage, statsTotalPages);
  const paginatedPlayerStats = filteredSortedPlayerStats.slice(
    (statsSafePage - 1) * TABLE_PAGE_SIZE,
    statsSafePage * TABLE_PAGE_SIZE
  );
  const statsRangeStart = filteredSortedPlayerStats.length === 0
    ? 0
    : (statsSafePage - 1) * TABLE_PAGE_SIZE + 1;
  const statsRangeEnd = Math.min(statsSafePage * TABLE_PAGE_SIZE, filteredSortedPlayerStats.length);

  const formatPlaytime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hrs}h ${mins}m`;
  };

  const updateGestionFilter = <K extends keyof GestionFilters>(key: K, value: GestionFilters[K]) => {
    setGestionFilters((prev) => ({ ...prev, [key]: value }));
    setGestionPage(1);
  };

  const updateStatsFilter = <K extends keyof StatsFilters>(key: K, value: StatsFilters[K]) => {
    setStatsFilters((prev) => ({ ...prev, [key]: value }));
    setStatsPage(1);
  };

  const handleStatsSort = (column: StatsSortColumn) => {
    if (statsSortColumn === column) {
      setStatsSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setStatsSortColumn(column);
    setStatsSortDirection(column === 'email' || column === 'scenario' ? 'asc' : 'desc');
  };

  const renderSortIndicator = (column: StatsSortColumn) => {
    if (statsSortColumn !== column) return null;
    return (
      <span className={styles.sortIndicator}>
        {statsSortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const selectedCategoryLabel = selectedDepartment
    ? getDepartmentLabel(selectedDepartment, departmentOptions)
    : null;

  const categoryLabels = departmentOptions.map((dept) => dept.label);
  const visibleCategories = selectedCategoryLabel ? [selectedCategoryLabel] : categoryLabels;

  const radarChartData = useMemo(() => radarDataApi.map((item) => {
    const datum: RadarDatum = { axis: item.label, fullMark: 100 };
    departmentOptions.forEach((dept) => {
      const entry = item.byDepartment.find((d) => d.key === dept.key);
      datum[dept.label] = entry?.score ?? 0;
    });
    datum['Moyenne groupe'] = item.groupAverage;
    return datum;
  }), [radarDataApi, departmentOptions]);

  const hasPedaData = scenarioErrors.length > 0
    || missionErrors.length > 0
    || radarDataApi.some((item) => item.groupAverage > 0);

  const fetchAnalytics = useCallback(async (options?: { silent?: boolean }) => {
    if (!authUser) return;
    if (!options?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedStory) params.set('scenario_id', selectedStory);
      if (selectedDepartment) params.set('department', selectedDepartment);
      const response = await apiFetch(`/api/stats/analytics?${params.toString()}`, {
        headers: {
          ...getActiveWorkspaceHeaders(),
        },
      });
      const data: AnalyticsResponse = await response.json();
      if (!response.ok) {
        throw new Error((data as { error?: string }).error || 'Échec du chargement des analytics');
      }
      setStories(data.stories);
      setEnterprise(data.enterprise ?? null);
      setPlayerStats(data.playerStats ?? []);
      setAvgProgressByScenario(data.avgProgressByScenario ?? []);
      setAvgProgressByMission(data.avgProgressByMission ?? []);
      setKpis(data.kpis);
      setWeeklyLaunches(data.weeklyLaunches);
      setWeeklyActive(data.weeklyActive);
      setScenarioErrors(data.scenarioErrors ?? []);
      setMissionErrors(data.missionErrors ?? []);
      setRadarDataApi(data.radarData ?? []);
      setUsers(data.users);
      if (data.kpis?.byDepartment?.length) {
        setDepartmentOptions(
          data.kpis.byDepartment.map((item) => ({ key: item.key, label: item.label }))
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [authUser, selectedStory, selectedDepartment, workspaceId]);

  useEffect(() => {
    initialStorySet.current = false;
    setSelectedStory('');
  }, [workspaceId]);

  useEffect(() => {
    if (!initialStorySet.current && storiesForSelector.length > 0) {
      setSelectedStory(storiesForSelector[0].id);
      initialStorySet.current = true;
    }
  }, [storiesForSelector]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    if (selectedDepartment && !departmentOptions.some((dept) => dept.key === selectedDepartment)) {
      setSelectedDepartment(null);
    }
  }, [departmentOptions, selectedDepartment]);

  useEffect(() => {
    if (selectedStory && !storiesForSelector.some((story) => story.id === selectedStory)) {
      setSelectedStory(storiesForSelector[0]?.id ?? '');
    }
  }, [selectedStory, storiesForSelector]);

  const handleDepartmentClick = (key: string) => {
    setSelectedDepartment((prev) => (prev === key ? null : key));
  };

  const cancelEditingUser = () => {
    setEditingUser(null);
    setEditingDepartment('');
  };

  const startEditingUser = (user: AnalyticsUser) => {
    setUserUpdateError(null);
    setEditingUser(user.id);
    setEditingDepartment(user.department ?? departmentOptions[0]?.key ?? '');
  };

  const handleDepartmentSave = async (userId: string) => {
    if (!authUser || !editingDepartment) return;

    const currentUser = users.find((user) => user.id === userId);
    if (currentUser?.department === editingDepartment) {
      cancelEditingUser();
      return;
    }

    setSavingUserId(userId);
    setUserUpdateError(null);
    try {
      const response = await apiFetch(`/api/auth/admin/users/${userId}/department`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ department: editingDepartment }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Échec de la mise à jour');
      }
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, department: editingDepartment } : u))
      );
      cancelEditingUser();
      fetchAnalytics({ silent: true });
    } catch (err: unknown) {
      setUserUpdateError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSavingUserId(null);
    }
  };

  const handleActivateUser = async (userId: string) => {
    if (!authUser) return;
    setActivatingUserId(userId);
    setUserUpdateError(null);
    try {
      const response = await apiFetch(`/api/auth/admin/users/${userId}/activate`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Échec de l\'activation');
      }
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, active: 1 } : u))
      );
    } catch (err: unknown) {
      setUserUpdateError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setActivatingUserId(null);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    if (!authUser) return;
    if (!window.confirm('Désactiver ce compte ? L\'utilisateur ne pourra plus se connecter.')) return;
    setActivatingUserId(userId);
    setUserUpdateError(null);
    try {
      const response = await apiFetch(`/api/auth/admin/users/${userId}/deactivate`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Échec de la désactivation');
      }
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, active: 0 } : u))
      );
    } catch (err: unknown) {
      setUserUpdateError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setActivatingUserId(null);
    }
  };

  const handleResetPseudo = async (userId: string) => {
    if (!window.confirm('Réinitialiser ce pseudo ? Le joueur devra en choisir un autre à sa prochaine visite.')) return;
    setResettingPseudoId(userId);
    setUserUpdateError(null);
    try {
      await resetPlayerProfile(userId);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, pseudo: null } : u)));
    } catch (err: unknown) {
      setUserUpdateError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setResettingPseudoId(null);
    }
  };

  const departmentIndex = (key: string) => departmentOptions.findIndex((dept) => dept.key === key);
  const gestionColumnCount = 3 + (canToggleUserActive ? 1 : 0) + (leaderboardEnabled ? 1 : 0);

  return (
    <div className={styles.container}>
      {error && <div className={styles.errorBanner}>{error}</div>}
      {userUpdateError && <div className={styles.errorBanner}>{userUpdateError}</div>}
      {loading && <div className={styles.loadingBanner}>Chargement des données…</div>}

      {/* Header */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>
            Tableau de bord{enterprise?.name ? ` : ${enterprise.name}` : ''}
          </h1>
          {enterprise?.admin_code && (
            <p className={styles.adminCode}>
              Code Collaborateurs : <code>{enterprise.admin_code}</code>
            </p>
          )}
        </div>

        <div className={styles.storySelectorGroup}>
          <div className={styles.storySelector}>
            <label htmlFor="story-select">Histoire</label>
            <select
              id="story-select"
              value={selectedStory}
              onChange={(e) => setSelectedStory(e.target.value)}
              disabled={storiesForSelector.length === 0}
            >
              <option value="">Toutes les histoires</option>
              {storiesForSelector.length === 0 ? (
                <option value="" disabled>Aucune histoire disponible</option>
              ) : (
                storiesForSelector.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.releaseToProd === false ? ' (brouillon)' : ''}
                  </option>
                ))
              )}
            </select>
          </div>
          {showStoryVisibilityFilter && (
            <label className={styles.storyVisibilityFilter}>
              <input
                type="checkbox"
                checked={onlyPlayerVisibleStories}
                onChange={(e) => setOnlyPlayerVisibleStories(e.target.checked)}
              />
              Visibles joueurs uniquement
            </label>
          )}
        </div>
      </header>

      {/* KPI cards */}
      <section className={styles.kpiRow}>
        <div
          className={`${styles.kpiCard} ${selectedDepartment === null ? styles.kpiCardActive : ''}`}
          onClick={() => setSelectedDepartment(null)}
        >
          <span className={styles.kpiValue}>{kpis.total}</span>
          <span className={styles.kpiLabel}>Joueurs total</span>
        </div>
        {kpis.byDepartment.map((c, i) => (
          <div
            key={c.key}
            className={`${styles.kpiCard} ${styles.kpiCardClickable} ${selectedDepartment === c.key ? styles.kpiCardActive : ''}`}
            style={{
              borderTopColor: categoryColor(i),
              opacity: selectedDepartment === null || selectedDepartment === c.key ? 1 : 0.4,
            }}
            onClick={() => handleDepartmentClick(c.key)}
          >
            <span className={styles.kpiValue}>{c.count}</span>
            <span className={styles.kpiLabel}>{c.label}</span>
          </div>
        ))}
      </section>

      {/* Charts grid */}
      <section className={styles.chartsGrid}>
        {/* Chart 1 – Cumulative launches */}
        <div className={styles.chartCard}>
          <h3>Joueurs ayant lancé l'histoire (cumulé)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weeklyLaunches}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="week"
                stroke="rgba(255,255,255,0.4)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                angle={-30}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="rgba(255,255,255,0.4)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip {...tooltipStyle} />
              <Line
                type="monotone"
                dataKey="cumulative"
                name="Joueurs (cumulé)"
                stroke={COLORS.primary}
                strokeWidth={3}
                dot={{ r: 4, fill: COLORS.primary }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 2 – Weekly active players */}
        <div className={styles.chartCard}>
          <h3>Joueurs actifs par semaine</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={weeklyActive}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="week"
                stroke="rgba(255,255,255,0.4)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                angle={-30}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke="rgba(255,255,255,0.4)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip {...tooltipStyle} />
              <Bar
                dataKey="players"
                name="Joueurs actifs"
                fill={COLORS.secondary}
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 3 – Error rate by story or mission */}
        <div className={styles.chartCard}>
          <h3>
            {selectedStory
              ? "Taux d'erreur moyen par mission (%)"
              : "Taux d'erreur moyen par histoire (%)"}
          </h3>
          {errorChartData.length === 0 && !loading && (
            <p className={styles.emptyHint}>
              Aucune donnée — les statistiques apparaîtront quand les joueurs interagiront avec les missions.
            </p>
          )}
          <ResponsiveContainer width="100%" height={selectedStory ? Math.max(300, errorChartData.length * 52) : 300}>
            {selectedStory ? (
              <BarChart data={errorChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={12}
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  width={160}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                />
                <Tooltip
                  {...tooltipStyle}
                  formatter={(value: number) => [`${value}%`, "Taux d'erreur"]}
                />
                <Bar
                  dataKey="errorRate"
                  name="Taux d'erreur"
                  fill={COLORS.warning}
                  radius={[0, 6, 6, 0]}
                  barSize={20}
                />
              </BarChart>
            ) : (
              <BarChart data={errorChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="name"
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 100]}
                />
                <Tooltip
                  {...tooltipStyle}
                  formatter={(value: number) => [`${value}%`, "Taux d'erreur"]}
                />
                <Bar
                  dataKey="errorRate"
                  name="Taux d'erreur"
                  fill={COLORS.warning}
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Chart 4 – Average progress by story or mission */}
        <div className={styles.chartCard}>
          <h3>
            {selectedStory
              ? 'Taux de complétion par mission (%)'
              : 'Progression moyenne par histoire (%)'}
          </h3>
          {progressChartData.length === 0 && !loading && (
            <p className={styles.emptyHint}>Aucune donnée de progression disponible.</p>
          )}
          <ResponsiveContainer width="100%" height={selectedStory ? Math.max(300, progressChartData.length * 52) : 300}>
            {selectedStory ? (
              <BarChart data={progressChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={12}
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  width={160}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                />
                <Tooltip
                  {...tooltipStyle}
                  formatter={(value: number) => [`${value}%`, 'Complétion']}
                />
                <Bar
                  dataKey="progress"
                  name="Complétion"
                  fill={COLORS.success}
                  radius={[0, 6, 6, 0]}
                  barSize={20}
                />
              </BarChart>
            ) : (
              <BarChart data={progressChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="name"
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 100]}
                />
                <Tooltip
                  {...tooltipStyle}
                  formatter={(value: number) => [`${value}%`, 'Progression']}
                />
                <Bar
                  dataKey="progress"
                  name="Progression"
                  fill={COLORS.success}
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Chart 5 – Hex Radar */}
        <div className={`${styles.chartCard} ${styles.radarCard}`}>
          <h3>Compétences cyber par catégorie</h3>
          {!hasPedaData && !loading && (
            <p className={styles.emptyHint}>
              Aucune donnée — les statistiques apparaîtront quand les joueurs interagiront avec les missions.
            </p>
          )}
          <div className={styles.radarLegend}>
            {departmentOptions.map((dept, i) => {
              const label = dept.label;
              const isActive = selectedDepartment === null || selectedDepartment === dept.key;
              return (
                <button key={dept.key}
                  className={`${styles.legendBtn} ${selectedDepartment === dept.key ? styles.legendBtnActive : ''}`}
                  onClick={() => handleDepartmentClick(dept.key)}
                  style={{
                    '--cat-color': categoryColor(i),
                    opacity: isActive ? 1 : 0.35,
                  } as React.CSSProperties}
                >
                  <span className={styles.legendDot} style={{ background: categoryColor(i) }} />
                  {label}
                </button>
              );
            })}
            <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: COLORS.average, border: '1px solid rgba(255,255,255,0.4)' }} />
              Moyenne groupe
            </span>
          </div>
          <HexRadarChart
            data={radarChartData}
            categories={categoryLabels}
            categoryColors={COLORS.categories}
            visibleCategories={visibleCategories}
          />
        </div>
      </section>

      {showGestionTab && workspaceId && (
        <section className={styles.labelsSection}>
          <h2>Tags stats</h2>
          <p className={styles.emptyHint}>
            Personnalisez les noms des rôles affichés dans les statistiques. Un changement de nom ne réinitialise pas les choix existants.
          </p>
          <DepartmentLabelsEditor
            enterpriseId={workspaceId}
            onLabelsSaved={(nextDepartments) => {
              setDepartmentOptions(nextDepartments);
              void fetchAnalytics({ silent: true });
            }}
            onDepartmentsReset={() => { void fetchAnalytics({ silent: true }); }}
          />
        </section>
      )}

      {/* Users table */}
      <section className={styles.usersSection}>
        <div className={styles.usersSectionHeader}>
          <h2>Utilisateurs</h2>
          {showGestionTab && (
          <div className={styles.modeSwitch}>
            <button
              type="button"
              className={`${styles.modeSwitchBtn} ${userTableMode === 'gestion' ? styles.modeSwitchBtnActive : ''}`}
              onClick={() => setUserTableMode('gestion')}
            >
              Gestion
            </button>
            <button
              type="button"
              className={`${styles.modeSwitchBtn} ${userTableMode === 'stats' ? styles.modeSwitchBtnActive : ''}`}
              onClick={() => setUserTableMode('stats')}
            >
              Statistiques
            </button>
          </div>
          )}
        </div>
        <div className={styles.tableWrapper}>
          {showGestionTab && userTableMode === 'gestion' ? (
          <table className={`${styles.table} ${styles.gestionTable}`}>
            <thead>
              <tr>
                <th>Email</th>
                <th className={styles.categoryCol}>Catégorie</th>
                {leaderboardEnabled && <th>Pseudo</th>}
                {canToggleUserActive && <th>Statut</th>}
                <th className={styles.actionCol}>Action</th>
              </tr>
              <tr className={styles.filterRow}>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrer…"
                    value={gestionFilters.email}
                    onChange={(e) => updateGestionFilter('email', e.target.value)}
                    className={styles.filterInput}
                  />
                </th>
                <th className={styles.categoryCol}>
                  <select
                    value={gestionFilters.department}
                    onChange={(e) => updateGestionFilter('department', e.target.value)}
                    className={styles.filterInput}
                  >
                    <option value="">Toutes</option>
                    <option value={NO_DEPARTMENT_FILTER}>Non renseignée</option>
                    {departmentOptions.map((dept) => (
                      <option key={dept.key} value={dept.key}>{dept.label}</option>
                    ))}
                  </select>
                </th>
                {leaderboardEnabled && <th />}
                {canToggleUserActive && (
                  <th>
                    <select
                      value={gestionFilters.status}
                      onChange={(e) => updateGestionFilter('status', e.target.value as GestionFilters['status'])}
                      className={styles.filterInput}
                    >
                      <option value="">Tous</option>
                      <option value="active">Actif</option>
                      <option value="pending">En attente</option>
                    </select>
                  </th>
                )}
                <th className={styles.actionCol} />
              </tr>
            </thead>
            <tbody>
              {paginatedGestionUsers.length === 0 ? (
                <tr>
                  <td colSpan={gestionColumnCount} className={styles.noData}>
                    {users.length === 0
                      ? 'Aucun utilisateur pour le moment.'
                      : 'Aucun utilisateur pour ces filtres.'}
                  </td>
                </tr>
              ) : paginatedGestionUsers.map((user) => {
                const deptKey = user.department ?? '';
                const deptIdx = departmentIndex(deptKey);
                const deptLabel = getDepartmentLabel(user.department, departmentOptions);
                const isEditing = editingUser === user.id;
                const isSaving = savingUserId === user.id;
                const isTogglingActive = activatingUserId === user.id;
                const isActive = user.active !== 0;
                return (
                <tr key={user.id}>
                  <td className={styles.emailCell}>{user.email}</td>
                  <td className={styles.categoryCol}>
                    {isEditing ? (
                      <select
                        value={editingDepartment}
                        onChange={(e) => setEditingDepartment(e.target.value)}
                        disabled={isSaving}
                        autoFocus
                        className={styles.categorySelect}
                      >
                        {departmentOptions.map((dept) => (
                          <option key={dept.key} value={dept.key}>{dept.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={styles.categoryBadge}
                        style={deptIdx >= 0 ? {
                          backgroundColor: `${categoryColor(deptIdx)}22`,
                          color: categoryColor(deptIdx),
                          borderColor: `${categoryColor(deptIdx)}44`,
                        } : undefined}
                      >
                        {deptLabel}
                      </span>
                    )}
                  </td>
                  {leaderboardEnabled && (
                    <td>
                      {user.pseudo ? (
                        <span className={styles.pseudoCell}>
                          <span>{user.pseudo}</span>
                          <button
                            type="button"
                            className={styles.cancelBtn}
                            disabled={resettingPseudoId === user.id}
                            onClick={() => handleResetPseudo(user.id)}
                          >
                            {resettingPseudoId === user.id ? '…' : 'Réinitialiser'}
                          </button>
                        </span>
                      ) : (
                        <span className={styles.pseudoEmpty}>Pas encore choisi</span>
                      )}
                    </td>
                  )}
                  {canToggleUserActive && (
                    <td>{isActive ? 'Actif' : 'En attente'}</td>
                  )}
                  <td className={styles.actionCol}>
                    <div className={styles.rowActions}>
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            className={styles.saveBtn}
                            disabled={isSaving}
                            onClick={() => handleDepartmentSave(user.id)}
                          >
                            {isSaving ? '…' : 'Enregistrer'}
                          </button>
                          <button
                            type="button"
                            className={styles.cancelBtn}
                            disabled={isSaving}
                            onClick={cancelEditingUser}
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className={`${styles.editBtn}${!canToggleUserActive ? ` ${styles.actionBtnWide}` : ''}`}
                            disabled={editingUser !== null && editingUser !== user.id}
                            onClick={() => startEditingUser(user)}
                          >
                            Modifier
                          </button>
                          {canToggleUserActive && (
                            isActive ? (
                              <button
                                type="button"
                                className={styles.cancelBtn}
                                disabled={isTogglingActive}
                                onClick={() => handleDeactivateUser(user.id)}
                              >
                                {isTogglingActive ? '…' : 'Désactiver'}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={styles.saveBtn}
                                disabled={isTogglingActive}
                                onClick={() => handleActivateUser(user.id)}
                              >
                                {isTogglingActive ? '…' : 'Activer'}
                              </button>
                            )
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
          ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th
                  className={styles.sortableTh}
                  onClick={() => handleStatsSort('email')}
                >
                  Joueur{renderSortIndicator('email')}
                </th>
                <th
                  className={styles.sortableTh}
                  onClick={() => handleStatsSort('scenario')}
                >
                  Histoire{renderSortIndicator('scenario')}
                </th>
                <th
                  className={styles.sortableTh}
                  onClick={() => handleStatsSort('playtime')}
                >
                  Temps de jeu{renderSortIndicator('playtime')}
                </th>
                <th
                  className={styles.sortableTh}
                  onClick={() => handleStatsSort('progress')}
                >
                  Progression{renderSortIndicator('progress')}
                </th>
                <th
                  className={styles.sortableTh}
                  onClick={() => handleStatsSort('updated_at')}
                >
                  Dernière activité{renderSortIndicator('updated_at')}
                </th>
              </tr>
              <tr className={styles.filterRow}>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrer…"
                    value={statsFilters.email}
                    onChange={(e) => updateStatsFilter('email', e.target.value)}
                    className={styles.filterInput}
                  />
                </th>
                <th>
                  <select
                    value={statsFilters.scenarioId}
                    onChange={(e) => updateStatsFilter('scenarioId', e.target.value)}
                    className={styles.filterInput}
                  >
                    <option value="">Toutes</option>
                    {storiesForSelector.map((story) => (
                      <option key={story.id} value={story.id}>{story.name}</option>
                    ))}
                  </select>
                </th>
                <th>
                  <select
                    value={statsFilters.playtime}
                    onChange={(e) => updateStatsFilter('playtime', e.target.value as StatsFilters['playtime'])}
                    className={styles.filterInput}
                  >
                    <option value="">Tous</option>
                    <option value="short">&lt; 30 min</option>
                    <option value="medium">30 min – 2 h</option>
                    <option value="long">&gt; 2 h</option>
                  </select>
                </th>
                <th>
                  <select
                    value={statsFilters.progress}
                    onChange={(e) => updateStatsFilter('progress', e.target.value as StatsFilters['progress'])}
                    className={styles.filterInput}
                  >
                    <option value="">Toutes</option>
                    <option value="0">0 %</option>
                    <option value="1-49">1 – 49 %</option>
                    <option value="50-99">50 – 99 %</option>
                    <option value="100">100 %</option>
                  </select>
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrer…"
                    value={statsFilters.updatedAt}
                    onChange={(e) => updateStatsFilter('updatedAt', e.target.value)}
                    className={styles.filterInput}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedPlayerStats.map((stat, index) => (
                <tr key={`${stat.email}-${stat.scenario_id}-${index}`}>
                  <td>{stat.email}</td>
                  <td>{storyNameById.get(stat.scenario_id) ?? stat.scenario_id}</td>
                  <td>{formatPlaytime(stat.playtime_seconds)}</td>
                  <td>
                    <div className={styles.progressCell}>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${stat.progress_percent}%` }}
                        />
                      </div>
                      <span>{stat.progress_percent}%</span>
                    </div>
                  </td>
                  <td>{new Date(stat.updated_at).toLocaleString()}</td>
                </tr>
              ))}
              {paginatedPlayerStats.length === 0 && (
                <tr>
                  <td colSpan={5} className={styles.noData}>
                    {playerStats.length === 0
                      ? 'Aucune donnée disponible pour le moment.'
                      : 'Aucun résultat pour ces filtres.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          )}
        </div>
        {showGestionTab && userTableMode === 'gestion' ? (
          <div className={styles.pagination}>
            <span>
              {filteredGestionUsers.length === 0
                ? '0 utilisateur'
                : `${gestionRangeStart}–${gestionRangeEnd} sur ${filteredGestionUsers.length}`}
            </span>
            <div className={styles.paginationBtns}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={gestionSafePage <= 1}
                onClick={() => setGestionPage((page) => Math.max(1, page - 1))}
              >
                Précédent
              </button>
              <span className={styles.pageIndicator}>
                {gestionSafePage} / {gestionTotalPages}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={gestionSafePage >= gestionTotalPages}
                onClick={() => setGestionPage((page) => Math.min(gestionTotalPages, page + 1))}
              >
                Suivant
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.pagination}>
            <span>
              {filteredSortedPlayerStats.length === 0
                ? '0 entrée'
                : `${statsRangeStart}–${statsRangeEnd} sur ${filteredSortedPlayerStats.length}`}
            </span>
            <div className={styles.paginationBtns}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={statsSafePage <= 1}
                onClick={() => setStatsPage((page) => Math.max(1, page - 1))}
              >
                Précédent
              </button>
              <span className={styles.pageIndicator}>
                {statsSafePage} / {statsTotalPages}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={statsSafePage >= statsTotalPages}
                onClick={() => setStatsPage((page) => Math.min(statsTotalPages, page + 1))}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default AnalyticsDashboard;
